import styled from "@emotion/styled";

import {
  H2Container as ContentSummaryH2Container,
  H3Container as ContentSummaryH3Container,
} from "shared/components/content/content-summary/styles";
import {
  fontRoboto,
  setFullViewportWidthStyles,
  setFullViewportWidthWithMaxPageWidthStyles,
  theme,
} from "shared/lib/styles";
import type { WithStickyStackedHeightProps } from "shared/lib/styles";

import { ArticleAuthorBlockContainer } from "scmp-app/components/article/article-author-block/styles";
import { ArticleHeaderAdSlot } from "scmp-app/components/article/article-header-ad-slot";
import { DescriptionContainer as ArticleImageDescriptionContainer } from "scmp-app/components/article/article-image/styles";
import { pageStyle } from "scmp-app/components/article/article-render/article-content/standard-styles";
import { StyledShareButton } from "scmp-app/components/content/content-share-widget/styles";
import {
  H2Container as ContentSubHeadlineH2Container,
  H3Container as ContentSubHeadlineH3Container,
} from "scmp-app/components/content/content-sub-headline/styles";
import {
  Heading1,
  Heading2,
  Heading3,
  Heading4,
} from "scmp-app/components/schema-render/common/heading/styles";
import { StyledParagraph } from "scmp-app/components/schema-render/common/paragraph/styles";

import { LandingBannerInfographic } from "./landing-banner-infographic";

export const InfographicArticleContainer = styled.div`
  /* padding-inline: 20px; */

  /* ${setFullViewportWidthStyles()} */
`;

export const InfographicArticleWrapper = styled.div`
  display: grid;
  grid:
    "landing-banner landing-banner landing-banner" min-content
    "summary-and-author summary-and-author summary-and-author" min-content
    "header-ad header-ad header-ad" min-content
    "content content content" min-content
    "paywall paywall paywall" min-content
    "content-footer-ad content-footer-ad content-footer-ad" min-content
    "comment comment social" min-content
    "author-detail author-detail author-detail" min-content
    "topic topic topic" min-content
    "footer-ad footer-ad footer-ad" min-content
    / minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr);

  inline-size: 100%;
  max-inline-size: 1456px;
  margin-inline: auto;

  ${theme.breakpoints.up("tablet")} {
    grid:
      "landing-banner landing-banner landing-banner" min-content
      "left . summary-and-author" min-content
      "left . header-ad" min-content
      "left . content" min-content
      "left . paywall" min-content
      "left . content-footer-ad" min-content
      "left . comment" min-content
      "left . social" min-content
      "left . author-detail" min-content
      "left . topic" min-content
      "left . footer-ad" min-content
      "left . ." min-content
      / 44px 20px minmax(0, 1fr);
  }

  ${theme.breakpoints.up("desktop")} {
    grid:
      "header-ad header-ad header-ad header-ad" min-content
      "landing-banner landing-banner landing-banner landing-banner" min-content
      "left summary-and-author summary-and-author ." min-content
      "left content content ." min-content
      "left paywall paywall ." min-content
      "left content-footer-ad content-footer-ad ." min-content
      "left comment social ." min-content
      "left author-detail author-detail ." min-content
      "left topic topic ." min-content
      "left . . ." min-content
      / minmax(0, 1fr) minmax(0, 400px) minmax(0, 280px) minmax(0, 1fr);
  }

  > *:empty {
    margin: 0;
    padding: 0;
  }
`;

export const AppBarScrollOverTracker = styled.div`
  grid-row: landing-banner-start / summary-and-author-end;
  grid-column: span 1;

  inline-size: 0;

  visibility: hidden;
`;

export const LandingBanner = styled(LandingBannerInfographic)`
  grid-area: landing-banner;

  ${theme.breakpoints.only("mobile")} {
    ${setFullViewportWidthStyles()}
  }
`;

export const Left = styled.div<WithStickyStackedHeightProps>`
  grid-area: left;

  position: sticky;
  inset-block-start: ${props => props.$stickyStackedHeight + 40}px;

  display: none;

  transition: inset-block-start 100ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;

  ${theme.breakpoints.up("tablet")} {
    display: flex;
    flex-flow: column nowrap;
    gap: 60px;
    align-items: center;
    align-self: flex-start;

    margin-block-start: 40px;
  }

  ${theme.breakpoints.up("desktop")} {
    padding-inline-end: 88px;
  }

  ${StyledShareButton} {
    svg {
      > :first-child {
        fill: #001246;
      }

      path {
        fill: #ffffff;
      }
    }
  }
`;

export const SpeechWrapper = styled.div`
  ${theme.breakpoints.only("mobile")} {
    display: flex;
    justify-content: center;
  }
`;

export const SummaryAndAuthor = styled.div`
  grid-area: summary-and-author;
  ${ArticleAuthorBlockContainer}, ${ContentSubHeadlineH2Container}, ${ContentSubHeadlineH3Container}, ${ContentSummaryH2Container}, ${ContentSummaryH3Container}, ${SpeechWrapper} {
    inline-size: 100%;
    max-inline-size: 680px;
    margin-inline: auto;
  }

  ${ArticleAuthorBlockContainer} {
    margin-block-end: 30px;

    ${theme.breakpoints.up("tablet")} {
      margin-block-end: 20px;
    }
  }

  ${ContentSubHeadlineH2Container}, ${ContentSubHeadlineH3Container}, ${ContentSummaryH2Container}, ${ContentSummaryH3Container} {
    margin-block-end: 32px;

    font-weight: 300;
    font-size: 24px;
    font-family: ${fontRoboto};
    line-height: 32px;

    list-style: disc;

    & ul {
      padding: 0;

      list-style: disc;

      li {
        margin-block-end: 12px;
        margin-inline-start: 22px;
      }
    }

    & li:before {
      display: none;
    }

    ${StyledParagraph} {
      display: list-item;
    }

    ${theme.breakpoints.only("mobile")} {
      font-size: 20px;
      line-height: 25px;
    }
  }

  ${theme.breakpoints.only("mobile")} {
    padding-block-end: 23px;
  }
`;

export const Content = styled.div`
  grid-area: content;

  margin-block-start: 32px;

  ${props => pageStyle(props)}

  ${Heading1}, ${Heading2}, ${Heading3}, ${Heading4} {
    text-shadow: 2px 2px #ffca05;

    & * {
      text-shadow: initial;
    }
  }

  ${ArticleImageDescriptionContainer} {
    line-height: 16px;
  }
`;

export const Comment = styled.div`
  grid-area: comment;

  margin-block-start: 52px;

  ${theme.breakpoints.up("tablet")} {
    max-inline-size: 335px;
  }
`;

export const Social = styled.div`
  grid-area: social;

  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-end;
  align-items: center;

  > *:empty {
    margin: 0;
    padding: 0;
  }

  ${theme.breakpoints.up("tablet")} {
    max-inline-size: 335px;
  }
`;

export const SocialMobile = styled.div`
  display: none;

  margin-block-start: 52px;

  ${theme.breakpoints.only("mobile")} {
    display: block;
  }

  && ${StyledShareButton} {
    inline-size: 44px;
    block-size: 44px;

    div {
      inline-size: 44px;
      block-size: 44px;
    }

    svg {
      inline-size: 44px;
      block-size: 44px;
      > :first-child {
        fill: #001246;
      }

      path {
        fill: #ffffff;
      }
    }
  }
`;

export const SocialDesktop = styled.div`
  display: none;

  margin-block-start: 52px;

  ${theme.breakpoints.up("tablet")} {
    display: block;
  }

  ${StyledShareButton} {
    svg {
      > :first-child {
        fill: #001246;
      }

      path {
        fill: #ffffff;
      }
    }
  }
`;

export const MoreOnThis = styled.div`
  grid-area: more-on-this;

  ${theme.breakpoints.only("mobile")} {
    margin-block-start: 32px;
  }

  ${theme.breakpoints.up("tablet")} {
    margin-block: 32px;
  }
`;

export const AuthorDetail = styled.div`
  grid-area: author-detail;

  margin-block-start: 32px;

  background: #fafafa;
  ${theme.breakpoints.up("tablet")} {
    margin-block-start: 40px;
    padding-block: 12px;
    padding-inline: 32px;
  }
`;

export const Topic = styled.div`
  grid-area: topic;

  margin-block-start: 20px;
  ${theme.breakpoints.up("tablet")} {
    margin-block-start: 40px;
  }
`;

export const HeaderAdSlot = styled(ArticleHeaderAdSlot)`
  grid-area: header-ad;

  display: none;

  ${theme.breakpoints.up("desktop")} {
    display: block;
  }
`;

export const ContentFooterAdSlot = styled.div`
  grid-area: content-footer-ad;
`;

export const FooterAdSlot = styled.div`
  grid-area: footer-ad;

  display: none;

  margin-block-start: 20px;

  ${theme.breakpoints.down("desktop")} {
    display: block;
  }

  ${theme.breakpoints.only("mobile")} {
    ${setFullViewportWidthWithMaxPageWidthStyles()}
  }
`;

export const Paywall = styled.div`
  grid-area: paywall;
`;
